import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

import { useMediaQuery } from 'usehooks-ts';

import type { GridTableData, GridTableHeaderItem } from '@uikit/index';

import { BaseCampaignLeaderBoardDesktop } from './BaseCampaignLeaderBoardDesktop';
import { BaseCampaignLeaderBoardMobile } from './BaseCampaignLeaderBoardMobile';

export interface BaseCampaignLeaderBoardProps<
  Data extends GridTableData = GridTableData,
  Meta = undefined,
> {
  columns: GridTableHeaderItem<Data, Meta>[];
  query: UseInfiniteQueryResult<InfiniteData<Data, unknown>, unknown>;
  items: Data[];
  currentUser: Data;
}

export const BaseCampaignLeaderBoard = <
  Data extends GridTableData = GridTableData,
  Meta = undefined,
>({
  columns,
  query,
  items,
  currentUser,
}: BaseCampaignLeaderBoardProps<Data, Meta>) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const { ref } = useInView({
    onChange: (inView) => inView && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });

  const showEmpty = !isLoading && !items?.length;
  const showLoader = isLoading || hasNextPage;

  const pagesLength = data?.pages.length ?? 0;

  const tableProps = useMemo(
    () => ({
      columns: columns as unknown as GridTableHeaderItem<GridTableData, unknown>[],
      items,
      showLoader,
      showEmpty,
      currentUser,
      pagesLength,
    }),
    [columns, items, showLoader, showEmpty, currentUser, pagesLength],
  );

  const Component = isMobile ? BaseCampaignLeaderBoardMobile : BaseCampaignLeaderBoardDesktop;

  return <Component {...tableProps} ref={ref} />;
};
